// =============================================================================
// src/views/camadas/index.ts — AppModule da Tela 05 (Estudio de Camadas).
//
// Proveniencia: markup FIEL de unified.html (linhas 851-963, section
//   data-section="camadas") + comportamento portado de Sections.camadas
//   (assets/unified-app.js 1391-1502). O markup mora aqui; o comportamento mora
//   em camadas-controller.ts (estado/listeners), camadas-presets.ts (cores) e
//   camadas-intensity.ts (marcadores de rotulo).
//
// Ciclo de vida (contrato SectionLifecycleModule): activate(ctx)/deactivate() sao
//   disparados pela SectionManager TS no scroll-spy (substitui Sections.camadas).
//   init(ctx) roda uma vez no bootstrap (ativa se o mapa ja existir). Guardamos
//   ctx.map em activate para que deactivate() (sem args) repasse o mapa ao controlador.
// =============================================================================

import type { AppContext, MapController } from '../../app/types';
import type { SectionLifecycleModule } from '../../core/section-manager';
import { camadasController } from './camadas-controller';
import { buildProvenanceFooterMarkup } from '../../data/data-provenance';

/** Markup da secao camadas (baseado no unified.html; divergencia consciente 2026-05-31:
 *  numero "05 · " removido do titulo a pedido do usuario). */
const CAMADAS_MARKUP = `    <section class="u-section" id="section-camadas" data-section="camadas">
      <div class="u-section-header">
        <div class="u-section-title">Estúdio de Camadas</div>
        <div class="u-section-subtitle">Composição multi-camada com análise de correlação socioeconômica</div>
      </div>
      <div class="u-section-body">
        <!-- Preset bar -->
        <div style="padding:8px 16px;border-bottom:0.5px solid var(--color-border-tertiary);display:flex;align-items:center;gap:8px">
          <span style="font-size:10px;color:var(--color-text-secondary);text-transform:uppercase;letter-spacing:0.5px;font-weight:500">Preset:</span>
          <span class="u-chip selected u-preset-pill" data-preset="voto">voto × vulnerab × ideb</span>
          <span class="u-chip u-preset-pill" data-preset="renda">renda × evang × pl</span>
          <span class="u-chip u-preset-pill" data-preset="pertencimento">pertenc × idade</span>
        </div>

        <div class="u-panel">
          <!-- Layer list -->
          <div style="display:flex;flex-direction:column;gap:6px">
            <!-- Layer 1 -->
            <div class="u-layer-item" data-preset="voto">
              <div class="u-layer-row-top">
                <i class="ti ti-lock u-layer-eye" style="font-size:13px;color:var(--color-text-tertiary)"></i>
                <span style="font-size:12px;font-weight:500;color:var(--color-text-primary);flex:1">Partido vencedor</span>
                <span style="font-size:9px;padding:1px 6px;background:var(--color-background-tertiary);border-radius:4px;color:var(--color-text-secondary)">base</span>
              </div>
              <div class="u-layer-controls">
                <div class="u-layer-opacity">
                  <span class="u-layer-opacity-val" style="font-size:10px;color:var(--color-text-secondary)">100%</span>
                  <input class="u-opacity-slider" type="range" value="100" style="flex:1;height:3px">
                </div>
                <div class="u-blend-pill">Normal <i class="ti ti-chevron-down" style="font-size:8px"></i></div>
              </div>
            </div>

            <!-- Layer 2 -->
            <div class="u-layer-item selected" data-preset="vulnerabilidade">
              <div class="u-layer-row-top">
                <i class="ti ti-eye u-layer-eye u-eye-toggle"></i>
                <span style="font-size:12px;font-weight:500;color:var(--color-text-primary);flex:1">Vulnerabilidade social</span>
                <i class="ti ti-grip-vertical" style="font-size:13px;color:var(--color-text-tertiary);cursor:grab"></i>
              </div>
              <div class="u-layer-controls">
                <div class="u-layer-opacity">
                  <span class="u-layer-opacity-val" style="font-size:10px;color:var(--color-text-secondary)">60%</span>
                  <input class="u-opacity-slider" type="range" value="60" style="flex:1;height:3px">
                </div>
                <div class="u-blend-pill">Multiply <i class="ti ti-chevron-down" style="font-size:8px"></i></div>
              </div>
            </div>

            <!-- Layer 3 -->
            <div class="u-layer-item" data-preset="renda">
              <div class="u-layer-row-top">
                <i class="ti ti-eye u-layer-eye u-eye-toggle"></i>
                <span style="font-size:12px;font-weight:500;color:var(--color-text-primary);flex:1">IDEB (educação)</span>
                <i class="ti ti-grip-vertical" style="font-size:13px;color:var(--color-text-tertiary);cursor:grab"></i>
              </div>
              <div class="u-layer-controls">
                <div class="u-layer-opacity">
                  <span class="u-layer-opacity-val" style="font-size:10px;color:var(--color-text-secondary)">40%</span>
                  <input class="u-opacity-slider" type="range" value="40" style="flex:1;height:3px">
                </div>
                <div class="u-blend-pill">Overlay <i class="ti ti-chevron-down" style="font-size:8px"></i></div>
              </div>
            </div>

            <!-- Layer 4 -->
            <div class="u-layer-item disabled" data-preset="pertencimento">
              <div class="u-layer-row-top">
                <i class="ti ti-eye-off u-layer-eye u-eye-toggle off"></i>
                <span style="font-size:12px;font-weight:500;color:var(--color-text-primary);flex:1">Pertencimento composto</span>
                <span class="u-belonging-config" style="font-size:9px;color:#185FA5;cursor:pointer">configurar</span>
              </div>
              <div class="u-layer-controls">
                <div class="u-layer-opacity">
                  <span class="u-layer-opacity-val" style="font-size:10px;color:var(--color-text-secondary)">0%</span>
                  <input class="u-opacity-slider" type="range" value="0" disabled style="flex:1;height:3px">
                </div>
                <div class="u-blend-pill">Índice <i class="ti ti-chevron-down" style="font-size:8px"></i></div>
              </div>
            </div>

            <!-- Add layer button -->
            <button style="padding:8px;border:0.5px dashed var(--color-border-secondary);border-radius:var(--border-radius-md);background:transparent;cursor:pointer;font-size:11px;color:var(--color-text-secondary);display:flex;align-items:center;justify-content:center;gap:6px;font-family:inherit" onclick="sendPrompt('Adicionar nova camada ao estúdio')">
              <i class="ti ti-plus"></i> adicionar camada
            </button>
          </div>

          <!-- Correlations -->
          <div>
            <div class="u-panel-section-label">Correlações detectadas</div>
            <div style="display:flex;flex-direction:column;gap:4px">
              <div style="display:flex;align-items:center;gap:8px;font-size:11px;padding:4px 0;border-bottom:0.5px solid var(--color-border-tertiary)">
                <div style="width:36px;font-weight:600;color:#3B6D11">r=0.81</div>
                <div style="flex:1;color:var(--color-text-primary)">Vulnerabilidade → voto direita</div>
                <div style="font-size:10px;color:var(--color-text-secondary)">forte</div>
              </div>
              <div style="display:flex;align-items:center;gap:8px;font-size:11px;padding:4px 0;border-bottom:0.5px solid var(--color-border-tertiary)">
                <div style="width:36px;font-weight:600;color:#854F0B">r=0.68</div>
                <div style="flex:1;color:var(--color-text-primary)">IDEB baixo → abstenção alta</div>
                <div style="font-size:10px;color:var(--color-text-secondary)">moderado</div>
              </div>
              <div style="display:flex;align-items:center;gap:8px;font-size:11px;padding:4px 0">
                <div style="width:36px;font-weight:600;color:#185FA5">r=0.54</div>
                <div style="flex:1;color:var(--color-text-primary)">Renda × pertencimento</div>
                <div style="font-size:10px;color:var(--color-text-secondary)">moderado</div>
              </div>
            </div>
          </div>

          <button class="u-cta-btn" onclick="sendPrompt('Gerar relatório de correlações das camadas')"><i class="ti ti-chart-scatter"></i> Exportar análise de correlações</button>
        </div>
      </div>
      ${buildProvenanceFooterMarkup('camadas')}
    </section>`;

/** Ultimo mapa visto em activate, para deactivate() (sem args) repassar ao controlador. */
let camadasActiveMap: MapController | null = null;

/**
 * AppModule da secao Camadas com hooks de ciclo de vida (porte de Sections.camadas).
 * render() emite o markup; activate/deactivate sao chamados pela SectionManager no
 * scroll-spy; init(ctx) cobre o bootstrap inicial (ativa se o mapa ja estiver pronto).
 */
export const camadasView: SectionLifecycleModule = {
  id: 'camadas',
  render(): string {
    return CAMADAS_MARKUP;
  },
  // Ativacao dirigida SO pelo SectionManager (scroll-spy). init NAO ativa: senao a
  // secao aplica seus efeitos no mapa compartilhado ja no boot, fora de vista. 2026-05-31.
  init(): void {},
  activate(ctx: AppContext): void {
    if (!ctx.map) return;
    camadasActiveMap = ctx.map;
    camadasController.activate(ctx.map);
  },
  deactivate(): void {
    if (camadasActiveMap) camadasController.deactivate(camadasActiveMap);
  },
};

export default camadasView;
